import { useMemo, useState } from 'react';

import { SelectOption } from '@/shared/types';

import { WeaponMode, Weapon_v2, WeaponType } from '@/api/types';

import { erzStatusName, erzTypeName } from '../uk.l10n';

interface FilterResult {
  filterStatusOptions: Array<SelectOption<NonNullable<FilterResult['selectedStatus']>>>;
  filterTypeOptions: Array<SelectOption<NonNullable<FilterResult['selectedType']>>>;
  filteredWeapons: Weapon_v2[];
  selectedStatus: Nullable<WeaponMode>;
  selectedType: Nullable<WeaponType>;
  setSelectedStatus: (state: Nullable<WeaponMode>) => void;
  setSelectedType: (state: Nullable<WeaponType>) => void;
  resetFilters: () => void;
}

export const useWeaponFilter = (weapons: Weapon_v2[]): FilterResult => {
  const [selectedStatus, setSelectedStatus] = useState<Nullable<WeaponMode>>(null);
  const [selectedType, setSelectedType] = useState<Nullable<WeaponType>>(null);

  const filterStatusOptions = useMemo<FilterResult['filterStatusOptions']>(() => {
    return Array.from(
      new Set(
        weapons
          .map((weapon) => weapon.statuses[0])
          .filter((status): status is WeaponMode => {
            return status !== undefined && Object.values(WeaponMode).includes(status as WeaponMode);
          }),
      ),
    ).reduce<FilterResult['filterStatusOptions']>((acc, objectMode) => {
      return [
        ...acc,
        {
          id: objectMode,
          label: erzStatusName[objectMode],
        },
      ];
    }, []);
  }, [weapons]);

  const filterTypeOptions = useMemo<FilterResult['filterTypeOptions']>(() => {
    return Array.from(new Set(weapons.map((weapon) => weapon.weaponType))).reduce<FilterResult['filterTypeOptions']>(
      (acc, weaponType) => {
        return [
          ...acc,
          {
            id: weaponType,
            label: erzTypeName[weaponType],
          },
        ];
      },
      [],
    );
  }, [weapons]);

  const filteredWeapons = useMemo<Weapon_v2[]>(() => {
    return weapons.filter((weapon) => {
      const statusMatch = !selectedStatus || weapon.statuses[0] === selectedStatus;
      const typeMatch = !selectedType || weapon.weaponType === selectedType;

      return statusMatch && typeMatch;
    });
  }, [weapons, selectedStatus, selectedType]);

  const resetFilters = (): void => {
    setSelectedStatus(null);
    setSelectedType(null);
  };

  return {
    filterStatusOptions,
    filterTypeOptions,
    filteredWeapons,
    selectedStatus,
    selectedType,
    setSelectedType,
    setSelectedStatus,
    resetFilters,
  };
};
